/* eslint-disable max-lines-per-function */
/* eslint-disable functional/prefer-readonly-type */
import { useHookstate } from '@hookstate/core'
import React, { useEffect, useMemo } from 'react'
import ConfirmDialog from '~/components/ConfirmDialog'
import CrudIndex from '~/components/CrudIndex'
import { ActionButtonsProps } from '~/components/CrudIndex/interfaces/ICrudIndex'
import Layout from '~/components/Layout'
import { useLang } from '~/hooks/useLang'
import { authFetch } from '~/services/fetch'
import notify from '~/utils/notify'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined'

export default function Index() {
  const { translate } = useLang()

  const forceRefresh = useHookstate(false)
  const openDialogConfirmDisabled = useHookstate(false)
  const subtypeName = useHookstate('')
  const subtypeActionId = useHookstate<number | null>(null)
  const loading = useHookstate<boolean>(false)
  const isEnabled = useHookstate(false)
  const name = useHookstate<string | null>(null)
  const code = useHookstate<string | null>(null)
  const priority = useHookstate<number | null>(null)
  const active = useHookstate<boolean | null>(null)

  useEffect(() => {
    document.title = translate('Administration - Dispatch System')
  }, [])

  const subtypeFilter = [
    {
      name: 'name',
      type: 'name',
      placeholder: 'Name',
      variable: name,
    },
    {
      name: 'code',
      type: 'code',
      placeholder: 'Code',
      variable: code,
    },
    {
      name: 'priority',
      type: 'select',
      placeholder: 'Priority',
      variable: priority,
      options: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    },
    {
      name: 'active',
      type: 'boolean',
      placeholder: 'Active',
      variable: active,
    },
  ]

  const disabledSubtype = (subtypeId) => {
    loading.set(true)
    authFetch({
      url: `/subtype/${subtypeId}/activate-or-deactivate`,
      method: 'POST',
    })
      .then((response) => {
        if (response.data.active) {
          notify({
            message: translate('activated with success!', {
              crudName: 'Subtipo',
            }),
            type: 'success',
          })
        } else {
          notify({
            message: translate('deactivated with success!', {
              crudName: 'Subtipo',
            }),
            type: 'success',
          })
        }
      })
      .finally(() => {
        openDialogConfirmDisabled.set(false)
        loading.set(false)
        forceRefresh.set(!forceRefresh.get())
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const handleDialogConfirmDisabled = () => {
    openDialogConfirmDisabled.set(false)
  }

  const handleDialogConfirmOpenDisabled = (subtypeId) => {
    openDialogConfirmDisabled.set(true)
    subtypeActionId.set(subtypeId)
  }

  const extraSubtypeActions = useMemo((): ActionButtonsProps[] => {
    return [
      {
        iconButton: (params) =>
          params['active'] === 'Sim' ? (
            <ToggleOnIcon />
          ) : (
            <ToggleOffOutlinedIcon />
          ),
        label: (params) =>
          params['active'] === 'Sim'
            ? translate('Deactivate', { crudName: 'subtipo' })
            : translate('Activate', { crudName: 'subtipo' }),
        action: (params) => {
          handleDialogConfirmOpenDisabled(params.id)

          params['active'] === 'Sim'
            ? isEnabled.set(true)
            : isEnabled.set(false)
          subtypeName.set(params['name'])
        },
        showInMenu: function () {
          return false
        },
      },
    ]
  }, [])

  return (
    <Layout marginLayout={true}>
      <CrudIndex
        model="subtype"
        title="Subtipo de Ocorrência"
        disableColumnsFilters
        searchButton={() => forceRefresh.set(!forceRefresh.get())}
        forceRefresh={forceRefresh.get()}
        filters={subtypeFilter}
        clearFilters={() => {
          name.set(null)
          code.set(null)
          priority.set(null)
          active.set(null)

          forceRefresh.set(!forceRefresh.get())
        }}
        aliasLabel={[{ column: 'type:name', label: 'Type' }]}
        booleanField={['active', 'ref_to_vehicle']}
        numberField={[
          'mandatory_vehicle',
          'mandatory_involved',
          'mandatory_weapon',
          'mandatory_organization',
          'mandatory_drug',
          'mandatory_object',
          'mandatory_animal',
        ]}
        notSortable={[
          'code',
          'ref_to_vehicle',
          'mandatory_vehicle',
          'mandatory_involved',
          'mandatory_weapon',
          'mandatory_organization',
          'mandatory_drug',
          'mandatory_object',
          'mandatory_animal',
          'active',
        ]}
        forceOrderColumns={[
          'name',
          'code',
          'priority',
          'active',
        ]}
        exceptColumns={[
          'type_id',
          'ref_to_vehicle',
          'mandatory_vehicle',
          'mandatory_involved',
          'mandatory_weapon',
          'mandatory_organization',
          'mandatory_drug',
          'mandatory_object',
          'mandatory_animal',
        ]}
        canView={true}
        viewTitle="Subtipo de Ocorrência"
        showInViewMore={[
          'name',
          'code',
          'priority',
          'ref_to_vehicle',
          'mandatory_vehicle',
          'mandatory_involved',
          'mandatory_weapon',
          'mandatory_organization',
          'mandatory_drug',
          'mandatory_object',
          'mandatory_animal',
          'active',
          'created_at',
        ]}
        extraActions={extraSubtypeActions}
        deleteable={false}
      />
      <ConfirmDialog
        handleAgree={() => disabledSubtype(subtypeActionId.get())}
        handleClose={() => handleDialogConfirmDisabled()}
        open={openDialogConfirmDisabled}
        title={translate('Confirmation')}
        content={
          isEnabled.get()
            ? translate('Confirm the deactivation of the?', {
                crudName: 'subtipo',
                name: subtypeName.get(),
              })
            : translate('Confirm the activation?', {
                crudName: 'subtipo',
                name: subtypeName.get(),
              })
        }
        loading={loading}
      />
    </Layout>
  )
}
